import LegalPageLayout from "@/components/LegalPageLayout";
import usePageMeta from "@/hooks/usePageMeta";

const SUPPORT_EMAIL = "info@mayr-technologies.com";

const SectionCard = ({ heading, children }) => (
  <section className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 md:p-7">
    <h2 className="font-['Outfit'] text-xl md:text-2xl tracking-tight text-white">
      {heading}
    </h2>
    <div className="mt-3 font-['Manrope'] text-sm md:text-base leading-relaxed text-neutral-400 max-w-3xl">
      {children}
    </div>
  </section>
);

const PrivacyPage = () => {
  usePageMeta(
    "Privacy Policy | Mayr Technologies",
    "Learn how Mayr Technologies collects, uses, and protects information across its website and services.",
  );

  return (
    <LegalPageLayout
      title="Privacy Policy"
      intro="Mayr Technologies respects your privacy and is committed to protecting your personal information."
    >
      <SectionCard heading="Information We Collect">
        <p>
          We may collect personal information that you provide directly to us, including your name, email address, billing information, account information, and any information you submit through our website, services, or communications.
        </p>
        <p className="mt-3">
          We may also collect technical and usage information such as browser type, device information, IP address, pages viewed, and interactions with our website or platforms.
        </p>
      </SectionCard>

      <SectionCard heading="How We Use Information">
        <ul className="list-disc pl-5 space-y-2">
          <li>provide and operate our services</li>
          <li>communicate with you</li>
          <li>respond to support requests and inquiries</li>
          <li>process transactions</li>
          <li>improve our website, products, and services</li>
          <li>maintain security and prevent misuse</li>
        </ul>
      </SectionCard>

      <SectionCard heading="Sharing of Information">
        <p>
          We do not sell your personal information. We may share information with trusted service providers and infrastructure partners only as necessary to operate our business, process payments, host services, provide communications, or comply with legal obligations.
        </p>
      </SectionCard>

      <SectionCard heading="Data Retention">
        <p>
          We retain information only as long as reasonably necessary for business, legal, accounting, or operational purposes.
        </p>
      </SectionCard>

      <SectionCard heading="Security">
        <p>
          We take reasonable technical and organizational measures to protect personal information, but no method of transmission or storage is completely secure.
        </p>
      </SectionCard>

      <SectionCard heading="Third-Party Services">
        <p>
          Our website or services may rely on third-party providers, including hosting, communications, analytics, payment processing, and related infrastructure. Those providers may process data according to their own policies.
        </p>
      </SectionCard>

      <SectionCard heading="Your Choices">
        <p>
          You may contact us regarding access, correction, or general privacy questions at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-neutral-200 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </SectionCard>

      <SectionCard heading="Contact">
        <p>Mayr Technologies</p>
        <p className="mt-2">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-neutral-200 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
        <p className="mt-2">
          <a
            href="https://mayr-technologies.com"
            className="text-neutral-200 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
          >
            https://mayr-technologies.com
          </a>
        </p>
      </SectionCard>

      <p className="pt-2 text-xs md:text-sm text-neutral-500 font-['Manrope']">Last updated: March 2026</p>
    </LegalPageLayout>
  );
};

export default PrivacyPage;
